import { Form } from 'antd';
import { type InputRef, Input } from 'antd5';
import { isEmpty, isNil, omitBy, pick } from 'lodash';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type TDisplayColumnData } from 'components/data/types';
import { removeDefaultFilters } from 'components/DataTable/Views/utils';
import { Button, Modal, ModalActionButtons, useMessage } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import {
  type AtlasGqlTableView,
  type AtlasGqlTableViewsQuery,
  TableViewsDocument,
  useCreateTableViewMutation,
  useTableViewsQuery,
  useUpdateTableViewMutation,
} from 'types/atlas-graphql';

import { Summary } from '../Summary';
import { StyledCollapse } from '../Views.style';

const { Panel } = StyledCollapse;

const NAME_CHARACTER_RANGE = { min: 3, max: 100 };

export enum ESaveOptions {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  COPY = 'COPY',
}

export type SaveViewState = {
  isOpen: boolean;
  tableId?: string;
  tableView?: Partial<AtlasGqlTableView>;
  columnDefs?: TDisplayColumnData[];
  type?: ESaveOptions;
};

type SaveViewModalProps = {
  saveViewState: SaveViewState;
  onClose: () => void;
  onSuccess?: (view: AtlasGqlTableView) => void;
  onError?: (error: Error) => void;
};

export const SaveViewModal: React.FunctionComponent<SaveViewModalProps> = ({
  saveViewState,
  onClose,
  onSuccess,
  onError,
}) => {
  const message = useMessage();
  const { t } = useTranslation();
  const inputRef = useRef<InputRef>(null);
  const { isOpen, tableId, tableView, columnDefs, type = ESaveOptions.CREATE } = saveViewState;

  const [form] = Form.useForm();

  // load existing views
  const { data: tableViewsData, loading: tableViewsLoading } = useTableViewsQuery({
    variables: { tableName: tableId },
    skip: !tableId,
  });

  // create table view mutation
  const [createTableView, { loading: createTableViewLoading }] = useCreateTableViewMutation({
    onCompleted: data => {
      const createdTableView = data.createTableView;
      if (createdTableView) {
        message.success(
          t('components.viewSavedSuccess', '{{name}} saved successfully', {
            name: createdTableView.name,
          })
        );
        onSuccess && onSuccess(createdTableView);
      }

      form.resetFields();
      onClose();
    },
    onError: error => {
      message.error(
        t('components.viewSaveError', 'There was an error saving the view. Please try again.')
      );
      onError && onError(error);

      form.resetFields();
      onClose();
    },
    update: (cache, { data }) => {
      const createdTableView = data?.createTableView;
      if (createdTableView) {
        cache.updateQuery(
          {
            query: TableViewsDocument,
            variables: { tableName: tableId },
          },
          (data: AtlasGqlTableViewsQuery | undefined) => {
            return data
              ? {
                  ...data,
                  tableViews: [...(data.tableViews ?? []), createdTableView],
                }
              : data;
          }
        );
      }
    },
  });

  // update table view mutation
  const [updateTableView, { loading: updateTableViewLoading }] = useUpdateTableViewMutation({
    onCompleted: data => {
      const updatedTableView = data.updateTableView;
      if (updatedTableView) {
        message.success(
          t('components.viewSavedSuccess', '{{name}} saved successfully', {
            name: updatedTableView.name,
          })
        );
        onSuccess && onSuccess(updatedTableView);
      }

      form.resetFields();
      onClose();
    },
    onError: error => {
      message.error(
        t('components.viewSaveError', 'There was an error saving the view. Please try again.')
      );
      onError && onError(error);

      form.resetFields();
      onClose();
    },
  });

  // handle submit logic
  const handleSubmit = ({ name: _name }: { name: string }) => {
    const name = _name.trim();
    const { id: viewId, filters, sorts, columns, multiSorts, customFields } = tableView ?? {};

    const filtersToSave = removeDefaultFilters(tableId, filters);
    const sortsToSave = omitBy(pick(sorts, ['columnKey', 'order']), isNil);

    const viewToSave = {
      ...(!isEmpty(filtersToSave) ? { filters: filtersToSave } : {}),
      ...(!isEmpty(sortsToSave) ? { sorts: sortsToSave } : {}),
      ...(!isEmpty(columns) ? { columns } : {}),
      ...(!isEmpty(multiSorts) ? { multiSorts } : {}),
      ...(!isEmpty(customFields) ? { customFields } : {}),
    };

    if ([ESaveOptions.CREATE, ESaveOptions.COPY].includes(type) && !!tableId) {
      createTableView({
        variables: {
          input: {
            tableName: tableId,
            name,
            type: tableView?.type,
            ...viewToSave,
          },
        },
      });
    } else if ([ESaveOptions.UPDATE].includes(type) && !!viewId) {
      updateTableView({
        variables: {
          input: {
            id: viewId,
            ...viewToSave,
          },
        },
      });
    }
  };

  // handler to close and reset form
  const onFormClose = () => {
    form.resetFields();
    onClose();
  };

  // update name field
  useEffect(() => {
    if (isOpen && form) {
      form.setFieldsValue({
        name: !!tableView?.name
          ? type === ESaveOptions.UPDATE
            ? tableView.name
            : type === ESaveOptions.COPY
              ? `${tableView.name} - Copy`
              : undefined
          : undefined,
      });
    }
  }, [isOpen, form, tableView?.name, type]);

  return (
    <Modal
      open={isOpen && !!tableView}
      title={t('components.saveView', 'Save View')}
      onCancel={onClose}
      destroyOnClose
      footer={null}
    >
      <AutoFocus elementToFocus={inputRef}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label={t('components.viewName', 'View Name')}
            rules={[
              {
                message: t('components.inputRequired', 'Input is required.'),
                required: true,
                whitespace: true,
                validateTrigger: 'onFinish',
              },
              {
                message: t(
                  'components.inputCharacterRange',
                  'Input must be between {{min}} and {{max}} characters.',
                  { min: NAME_CHARACTER_RANGE.min, max: NAME_CHARACTER_RANGE.max }
                ),
                min: NAME_CHARACTER_RANGE.min,
                max: NAME_CHARACTER_RANGE.max,
                transform: value => value?.trim(),
                validateTrigger: 'onChange',
              },
              {
                message: t('components.viewNameExists', 'There is already a View with this name.'),
                transform: value => value?.trim(),
                validator(_, value) {
                  if (
                    type !== ESaveOptions.UPDATE &&
                    (tableViewsData?.tableViews ?? [])
                      .map(tableView => tableView.name?.toLowerCase())
                      .includes(value?.toLowerCase())
                  ) {
                    return Promise.reject();
                  } else {
                    return Promise.resolve();
                  }
                },
                validateTrigger: 'onFinish',
              },
            ]}
          >
            <Input
              ref={inputRef}
              disabled={type === ESaveOptions.UPDATE}
              placeholder={t('components.enterViewName', 'Enter a view name')}
            />
          </Form.Item>
          <StyledCollapse>
            <Panel
              key={'view_conditions'}
              header={t('components.viewConditions', 'View Conditions')}
            >
              {tableView && columnDefs && <Summary tableView={tableView} columnDefs={columnDefs} />}
            </Panel>
          </StyledCollapse>
          <ModalActionButtons>
            <Button onClick={onFormClose}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={tableViewsLoading || createTableViewLoading || updateTableViewLoading}
            >
              {t('common.save', 'Save')}
            </Button>
          </ModalActionButtons>
        </Form>
      </AutoFocus>
    </Modal>
  );
};
